import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ShieldCheck, Loader2, CheckCircle2 } from "lucide-react";

export default function VolunteerActivate() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState("checking"); // checking | invalid | form | done
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accept, setAccept] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get(`/volunteer/invite/${token}`).then(({ data }) => { setEmail(data.email); setState("form"); }).catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try { await api.post("/volunteer/activate", { token, password, accept_policies: accept }); setState("done"); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6" data-testid="volunteer-activate-page">
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-xl shadow-sm p-8">
        {state === "checking" && <div className="py-10 flex justify-center"><Loader2 className="animate-spin" /></div>}
        {state === "invalid" && (
          <div className="text-center" data-testid="activate-invalid">
            <h1 className="font-display text-2xl font-bold text-[#1F4D2E] mb-2">Invitation not valid</h1>
            <p className="text-sm text-[#4F6B54]">This invitation is invalid or has expired. Please contact the Refugium Ethiopia team.</p>
            <Link to="/" className="inline-block mt-5 text-[#C1272D] font-semibold">Back to home</Link>
          </div>
        )}
        {state === "form" && (
          <>
            <div className="flex items-center gap-2 text-[#1F4D2E] mb-1"><ShieldCheck size={22} /><h1 className="font-admin text-2xl font-bold">Activate your volunteer account</h1></div>
            <p className="text-sm text-[#52525B] mb-6">Setting up <b>{email}</b>. Create a password and accept our volunteer policies. You'll set up two-factor authentication on first login.</p>
            {error && <div className="bg-red-50 text-red-700 text-sm rounded-md px-4 py-3 mb-4" data-testid="activate-error">{error}</div>}
            <form onSubmit={submit}>
              <input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Create a password (min 8 chars)" data-testid="activate-password" className="w-full border border-gray-200 rounded-md px-4 py-2.5 mb-4 focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]" />
              <label className="flex items-start gap-2 text-sm text-[#52525B] mb-5"><input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} data-testid="activate-accept" className="mt-0.5" /> I agree to the Refugium Ethiopia volunteer code of conduct, safeguarding and confidentiality policies.</label>
              <button disabled={busy || !accept} data-testid="activate-submit" className="w-full bg-[#0A0A0A] text-white rounded-md py-3 font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2">{busy ? <Loader2 className="animate-spin" size={18} /> : "Activate account"}</button>
            </form>
          </>
        )}
        {state === "done" && (
          <div className="text-center" data-testid="activate-done">
            <CheckCircle2 className="mx-auto text-[#1F4D2E] mb-3" size={40} />
            <h1 className="font-display text-2xl font-bold text-[#1F4D2E] mb-2">Account activated</h1>
            <p className="text-sm text-[#4F6B54] mb-6">Sign in to set up two-factor authentication and access your volunteer workspace.</p>
            <button onClick={() => nav("/login?portal=volunteer")} data-testid="activate-to-login" className="bg-[#1F4D2E] text-white rounded-full px-6 py-3 font-semibold">Sign in</button>
          </div>
        )}
      </div>
    </div>
  );
}
